type TEventParams = {
  bubbles?: boolean | undefined;
  cancelable: boolean | undefined;
  composed?: boolean | undefined;
  detail?: TEventDetail;
  node?: HTMLElement;
};

type TEventDetail = object | undefined | null;

/**
 * Dispatches a custom event with an optional detail value.
 *
 * @param {string} type Name of event type.
 * @param {*=} detail Detail value containing event-specific
 *   payload.
 * @param {{ bubbles: (boolean|undefined), cancelable: (boolean|undefined), composed: (boolean|undefined) }=}
 *  options Object specifying options.  These may include:
 *  `bubbles` (boolean, defaults to `true`),
 *  `cancelable` (boolean, defaults to false), and
 *  `node` on which to fire the event (HTMLElement, defaults to `this`).
 * @return {!Event} The new event that was fired.
 */

(function () {
  if (typeof window === 'undefined' || typeof window.CustomEvent === 'function') {
    return false;
  }

  function CustomEvent(event: string, params: TEventParams) {
    const newParams = params || { bubbles: false, cancelable: false, detail: undefined };
    const evt = document.createEvent('CustomEvent');
    evt.initCustomEvent(event, newParams.bubbles, newParams.cancelable, newParams.detail);
    evt.preventDefault = function () {
      Object.defineProperty(this, 'defaultPrevented', {
        get() {
          return true;
        },
      });
    };
    return evt;
  }

  CustomEvent.prototype = window.Event.prototype;

  // @ts-expect-error expect
  window.CustomEvent = CustomEvent;
  return undefined;
})();

function fireEvent(type: string, detail: TEventDetail, options: TEventParams) {
  const newOptions = options || {};
  const newDetail = detail === null || detail === undefined ? {} : detail;
  const event = new CustomEvent(type, {
    bubbles: newOptions.bubbles === undefined ? true : newOptions.bubbles,
    cancelable: Boolean(newOptions.cancelable),
    detail: newDetail,
  });
  const node = newOptions.node || document.getElementsByTagName('body')[0];
  node.dispatchEvent(event);

  return event;
}

export default fireEvent;
